import {
  createCourseFromTemplateApi,
  createSectionApi,
  addLectureApi,
  updateLessonQuizApi,
  updateFinalCourseQuizApi,
} from '../api/apiServices';

// Creates a full course (sections, lessons, and quizzes) from a
// "template"-shaped object -- title/description/category/... plus a
// `sections` array of `{ title, lectures: [...] }`. Reports progress as it
// goes since this is several sequential API calls and can take a few
// seconds for a template with many lessons.
//
// Used both by the ready-made course templates (Course Template Gallery)
// and by AI-generated course drafts (AiCourseGeneratorModal) -- either way
// the shape handed in is identical, so both features share this one,
// already-proven creation pipeline instead of duplicating it.
export async function applyCourseTemplate(template, instructorName, onProgress = () => {}, organizationId = null) {
  onProgress({ label: 'Creating course...' });
  const courseRes = await createCourseFromTemplateApi(template, instructorName, organizationId);
  if (!courseRes.success || !courseRes.course?._id) {
    throw new Error(courseRes.message || 'Failed to create the course');
  }
  const courseId = courseRes.course._id;

  // From here on, the course itself already exists -- any failure is
  // partial, so every error below carries the courseId with it, letting
  // the caller offer a link to finish the course manually instead of
  // just reporting a dead end.
  try {
    const totalLectures = template.sections.reduce((sum, sec) => sum + sec.lectures.length, 0);
    let lecturesDone = 0;

    for (let i = 0; i < template.sections.length; i++) {
      const section = template.sections[i];
      onProgress({ label: `Adding section ${i + 1} of ${template.sections.length}: "${section.title}"`, courseId });

      const sectionRes = await createSectionApi(courseId, section.title);
      if (!sectionRes.success || !sectionRes.section?._id) {
        throw new Error(`Adding section "${section.title}" failed`);
      }
      const sectionId = sectionRes.section._id;

      for (const lecture of section.lectures) {
        lecturesDone += 1;
        onProgress({ label: `Adding lesson ${lecturesDone} of ${totalLectures}: "${lecture.title}"`, courseId });

        const lectureRes = await addLectureApi(courseId, sectionId, lecture, {});
        if (!lectureRes.success || !lectureRes.lecture?._id) {
          throw new Error(`Adding lesson "${lecture.title}" failed`);
        }

        if (lecture.quiz) {
          onProgress({ label: `Saving quiz for "${lecture.title}"`, courseId });
          await updateLessonQuizApi(courseId, sectionId, lectureRes.lecture._id, lecture.quiz);
        }
      }
    }

    if (template.finalQuiz) {
      onProgress({ label: 'Setting the final course quiz...', courseId });
      await updateFinalCourseQuizApi(courseId, template.finalQuiz);
    }
  } catch (err) {
    const partialErr = new Error(`Course was created, but ${err.message || 'a step failed'}. You can finish setting it up manually.`);
    partialErr.courseId = courseId;
    partialErr.response = err.response;
    throw partialErr;
  }

  onProgress({ label: 'Done!', courseId });
  return courseId;
}
